export const EUR_RON_PAIR = "EUR/RON" as const;

export type FxCurveStatus = "draft" | "confirmed";

export const FX_FORWARD_INTERPOLATION_METHOD =
  "linear_outright_mid_by_month_ordinal_v1" as const;
export const FX_FORWARD_MAX_INTERPOLATED_GAP_MONTHS = 2;

export interface FxForwardInterpolationAnchor {
  deliveryMonth: string;
  bid: number | null;
  mid: number;
  ask: number | null;
}

export interface FxForwardPointDerivation {
  kind: "interpolated";
  method: typeof FX_FORWARD_INTERPOLATION_METHOD;
  leftAnchor: FxForwardInterpolationAnchor;
  rightAnchor: FxForwardInterpolationAnchor;
  /** Zero-to-one position of this month between the two exact anchors. */
  weight: number;
}

export interface FxForwardPoint {
  deliveryMonth: string;
  bid: number | null;
  mid: number;
  ask: number | null;
  /** Present only for a generated value; retained after draft confirmation. */
  derivation?: FxForwardPointDerivation;
}

export interface FxCurveHeader {
  id: string;
  pair: string;
  curveDate: string;
  source: string;
  notes: string | null;
  status: FxCurveStatus;
  version: number;
  createdByLabel: string | null;
  confirmedByLabel: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  confirmedAt: string | null;
}

export interface FxCurveDetail extends FxCurveHeader {
  points: FxForwardPoint[];
}

export interface FxActual {
  date: string;
  rate: number;
  source: string | null;
  fetchedAt: string | null;
}

export type FxEffectiveRow =
  | {
      deliveryMonth: string;
      status: "resolved";
      basis: "forward";
      rate: number;
      source: string | null;
      rateDate: string | null;
      curveId: string | null;
      curveDate: string | null;
      curveVersion: number | null;
      confirmedAt: string | null;
      bid: number | null;
      mid: number;
      ask: number | null;
    }
  | {
      deliveryMonth: string;
      status: "resolved";
      basis: "actual";
      rate: number;
      source: string | null;
      rateDate: string | null;
      actualTargetDate: string | null;
      fallbackDays: number | null;
    }
  | {
      deliveryMonth: string;
      status: "resolved";
      basis: "indicative";
      rate: number;
      source: string | null;
      rateDate: string | null;
      method: string | null;
      methodVersion: number | null;
      ageDays: number | null;
      maxAgeDays: number | null;
      fallbackReason: string | null;
      fallbackCurveId: string | null;
      fallbackCurveDate: string | null;
    }
  | {
      deliveryMonth: string;
      status: "missing";
      basis: "forward" | "actual" | "indicative";
      reason: string;
    };

export interface QuoteFxCurveSnapshot {
  pair: typeof EUR_RON_PAIR;
  curveId: string;
  curveVersion: number;
  curveDate: string;
  source: string;
  confirmedAt: string | null;
  appliedAt: string;
  rateSide: "mid";
  points: FxForwardPoint[];
}

export interface FxCurveMove {
  deliveryMonth: string;
  previousMid: number;
  nextMid: number;
  delta: number;
}

export interface FxCurveComparison {
  firstMonth: string | null;
  lastMonth: string | null;
  missingMonths: string[];
  comparableMonths: number;
  changedMonths: number;
  largestMoves: FxCurveMove[];
}

const recordOf = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const textOf = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

const numberOf = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function pointOf(value: unknown): FxForwardPoint | null {
  const row = recordOf(value);
  if (!row) return null;
  const deliveryMonth = textOf(row.deliveryMonth ?? row.month);
  const mid = numberOf(row.mid ?? row.rate);
  if (!deliveryMonth || !/^\d{4}-(0[1-9]|1[0-2])$/.test(deliveryMonth) || mid == null) {
    return null;
  }
  const derivation = fxForwardPointDerivationOf(row.derivation ?? row.provenance);
  return {
    deliveryMonth,
    bid: numberOf(row.bid),
    mid,
    ask: numberOf(row.ask),
    ...(derivation ? { derivation } : {}),
  };
}

function interpolationAnchorOf(value: unknown): FxForwardInterpolationAnchor | null {
  const row = recordOf(value);
  const deliveryMonth = textOf(row?.deliveryMonth);
  const mid = numberOf(row?.mid);
  if (!deliveryMonth || monthOrdinal(deliveryMonth) == null || mid == null) return null;
  return {
    deliveryMonth,
    bid: numberOf(row?.bid),
    mid,
    ask: numberOf(row?.ask),
  };
}

function fxForwardPointDerivationOf(value: unknown): FxForwardPointDerivation | null {
  const row = recordOf(value);
  if (
    row?.kind !== "interpolated"
    || row.method !== FX_FORWARD_INTERPOLATION_METHOD
  ) {
    return null;
  }
  const leftAnchor = interpolationAnchorOf(row.leftAnchor);
  const rightAnchor = interpolationAnchorOf(row.rightAnchor);
  const weight = numberOf(row.weight);
  if (!leftAnchor || !rightAnchor || weight == null || weight <= 0 || weight >= 1) {
    return null;
  }
  return {
    kind: "interpolated",
    method: FX_FORWARD_INTERPOLATION_METHOD,
    leftAnchor,
    rightAnchor,
    weight,
  };
}

function headerOf(value: unknown): FxCurveHeader | null {
  const row = recordOf(value);
  if (!row) return null;
  const id = textOf(row.id);
  const pair = textOf(row.pair);
  const curveDate = textOf(row.curveDate);
  const source = textOf(row.source);
  const version = numberOf(row.version);
  const status = row.status === "draft" || row.status === "confirmed" ? row.status : null;
  if (!id || !pair || !curveDate || !source || version == null || !status) return null;
  return {
    id,
    pair,
    curveDate,
    source,
    notes: textOf(row.notes),
    status,
    version,
    createdByLabel: textOf(row.createdByLabel ?? row.createdByName),
    confirmedByLabel: textOf(row.confirmedByLabel ?? row.confirmedByName),
    createdAt: textOf(row.createdAt),
    updatedAt: textOf(row.updatedAt),
    confirmedAt: textOf(row.confirmedAt),
  };
}

export function normalizeFxCurveList(value: unknown): FxCurveHeader[] {
  const root = recordOf(value);
  const source = Array.isArray(root?.curves) ? root.curves : Array.isArray(value) ? value : [];
  return source.map(headerOf).filter((row): row is FxCurveHeader => row != null);
}

export function normalizeFxCurveDetail(value: unknown): FxCurveDetail | null {
  const root = recordOf(value);
  const candidate = recordOf(root?.curve) ?? root;
  const header = headerOf(candidate);
  if (!header) return null;
  const rawPoints = Array.isArray(candidate?.points)
    ? candidate.points
    : Array.isArray(root?.points)
      ? root.points
      : [];
  const points = rawPoints
    .map(pointOf)
    .filter((row): row is FxForwardPoint => row != null)
    .sort((a, b) => a.deliveryMonth.localeCompare(b.deliveryMonth));
  return { ...header, points };
}

export function normalizeFxActuals(value: unknown): FxActual[] {
  const root = recordOf(value);
  const source = Array.isArray(root?.actuals)
    ? root.actuals
    : Array.isArray(root?.rates)
      ? root.rates
      : [];
  return source.flatMap((value) => {
    const row = recordOf(value);
    const date = textOf(row?.date ?? row?.rateDate);
    const rate = numberOf(row?.rate);
    if (!date || rate == null) return [];
    return [{
      date,
      rate,
      source: textOf(row?.source),
      fetchedAt: textOf(row?.fetchedAt),
    }];
  });
}

export function normalizeFxEffective(value: unknown): FxEffectiveRow[] {
  const root = recordOf(value);
  const source = Array.isArray(root?.rows)
    ? root.rows
    : Array.isArray(root?.effective)
      ? root.effective
      : [];
  const result: FxEffectiveRow[] = [];
  for (const value of source) {
    const row = recordOf(value);
    const deliveryMonth = textOf(row?.deliveryMonth ?? row?.month);
    const basis = row?.basis === "actual"
      ? "actual"
      : row?.basis === "forward"
        ? "forward"
        : row?.basis === "indicative"
          ? "indicative"
          : null;
    if (!deliveryMonth || !basis) continue;
    if (row?.status === "missing") {
      result.push({
        deliveryMonth,
        status: "missing",
        basis,
        reason: textOf(row.reason) ?? "No eligible FX fixing is available.",
      });
      continue;
    }
    const rate = numberOf(row?.rate);
    if (rate == null) continue;
    if (basis === "forward") {
      result.push({
        deliveryMonth,
        status: "resolved",
        basis,
        rate,
        source: textOf(row?.source),
        rateDate: textOf(row?.rateDate ?? row?.date),
        curveId: textOf(row?.curveId),
        curveDate: textOf(row?.curveDate),
        curveVersion: numberOf(row?.curveVersion),
        confirmedAt: textOf(row?.confirmedAt),
        bid: numberOf(row?.bid),
        mid: numberOf(row?.mid) ?? rate,
        ask: numberOf(row?.ask),
      });
    } else if (basis === "actual") {
      result.push({
        deliveryMonth,
        status: "resolved",
        basis,
        rate,
        source: textOf(row?.source),
        rateDate: textOf(row?.rateDate ?? row?.date),
        actualTargetDate: textOf(row?.actualTargetDate),
        fallbackDays: numberOf(row?.fallbackDays),
      });
    } else {
      result.push({
        deliveryMonth,
        status: "resolved",
        basis,
        rate,
        source: textOf(row?.source),
        rateDate: textOf(row?.rateDate ?? row?.date),
        method: textOf(row?.method),
        methodVersion: numberOf(row?.methodVersion),
        ageDays: numberOf(row?.ageDays),
        maxAgeDays: numberOf(row?.maxAgeDays),
        fallbackReason: textOf(row?.fallbackReason),
        fallbackCurveId: textOf(row?.fallbackCurveId),
        fallbackCurveDate: textOf(row?.fallbackCurveDate),
      });
    }
  }
  return result;
}

export function compareFxCurves(
  nextPoints: readonly FxForwardPoint[],
  previousPoints: readonly FxForwardPoint[] = []
): FxCurveComparison {
  const sorted = [...nextPoints].sort((a, b) => a.deliveryMonth.localeCompare(b.deliveryMonth));
  const firstMonth = sorted[0]?.deliveryMonth ?? null;
  const lastMonth = sorted[sorted.length - 1]?.deliveryMonth ?? null;
  const present = new Set(sorted.map((point) => point.deliveryMonth));
  const missingMonths: string[] = [];
  if (firstMonth && lastMonth) {
    for (let month = firstMonth; month <= lastMonth; month = addMonth(month, 1)) {
      if (!present.has(month)) missingMonths.push(month);
      if (month === lastMonth) break;
    }
  }

  const previousByMonth = new Map(
    previousPoints.map((point) => [point.deliveryMonth, point.mid] as const)
  );
  const moves = sorted.flatMap((point): FxCurveMove[] => {
    const previousMid = previousByMonth.get(point.deliveryMonth);
    if (previousMid == null) return [];
    return [{
      deliveryMonth: point.deliveryMonth,
      previousMid,
      nextMid: point.mid,
      delta: Number((point.mid - previousMid).toFixed(12)),
    }];
  });
  const changed = moves.filter((move) => Math.abs(move.delta) > 1e-12);

  return {
    firstMonth,
    lastMonth,
    missingMonths,
    comparableMonths: moves.length,
    changedMonths: changed.length,
    largestMoves: changed
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 8),
  };
}

export function quoteFxCurveSnapshot(curve: FxCurveDetail, appliedAt = new Date().toISOString()): QuoteFxCurveSnapshot {
  return {
    pair: EUR_RON_PAIR,
    curveId: curve.id,
    curveVersion: curve.version,
    curveDate: curve.curveDate,
    source: curve.source,
    confirmedAt: curve.confirmedAt,
    appliedAt,
    rateSide: "mid",
    // Quote snapshots intentionally freeze only executable rates. Point-level
    // derivation metadata remains on the source curve and must not leak into
    // the established Quote snapshot contract.
    points: curve.points.map((point) => ({
      deliveryMonth: point.deliveryMonth,
      bid: point.bid,
      mid: point.mid,
      ask: point.ask,
    })),
  };
}

/**
 * Rebuild draft-only interpolations between adjacent exact points.
 *
 * Existing derived rows are never anchors and are replaced from the current
 * exact inputs. A gap is filled only when one or two whole delivery months sit
 * between its anchors; longer gaps and leading/trailing months are not generated.
 */
export function fillMissingFxForwardMonths(
  points: readonly FxForwardPoint[]
): FxForwardPoint[] {
  const exactByMonth = new Map<string, FxForwardPoint>();
  for (const point of points) {
    if (point.derivation || monthOrdinal(point.deliveryMonth) == null) continue;
    // Draft validation rejects duplicate exact months. Keeping the last one
    // here makes the helper deterministic if it is called before validation.
    exactByMonth.set(point.deliveryMonth, exactPointCopy(point));
  }

  const exact = [...exactByMonth.values()]
    .sort((left, right) => left.deliveryMonth.localeCompare(right.deliveryMonth));
  const filled = new Map(exact.map((point) => [point.deliveryMonth, point] as const));

  for (let index = 0; index < exact.length - 1; index += 1) {
    const left = exact[index]!;
    const right = exact[index + 1]!;
    const leftOrdinal = monthOrdinal(left.deliveryMonth)!;
    const rightOrdinal = monthOrdinal(right.deliveryMonth)!;
    const missingCount = rightOrdinal - leftOrdinal - 1;
    if (
      missingCount < 1
      || missingCount > FX_FORWARD_MAX_INTERPOLATED_GAP_MONTHS
    ) {
      continue;
    }

    const ordinalSpan = rightOrdinal - leftOrdinal;
    for (let offset = 1; offset <= missingCount; offset += 1) {
      const weight = offset / ordinalSpan;
      const deliveryMonth = addMonth(left.deliveryMonth, offset);
      if (filled.has(deliveryMonth)) continue;
      filled.set(deliveryMonth, {
        deliveryMonth,
        bid: interpolateOptionalFxRate(left.bid, right.bid, weight),
        mid: interpolateFxRate(left.mid, right.mid, weight),
        ask: interpolateOptionalFxRate(left.ask, right.ask, weight),
        derivation: {
          kind: "interpolated",
          method: FX_FORWARD_INTERPOLATION_METHOD,
          leftAnchor: interpolationAnchorCopy(left),
          rightAnchor: interpolationAnchorCopy(right),
          weight,
        },
      });
    }
  }

  return [...filled.values()]
    .sort((left, right) => left.deliveryMonth.localeCompare(right.deliveryMonth));
}

function exactPointCopy(point: FxForwardPoint): FxForwardPoint {
  return {
    deliveryMonth: point.deliveryMonth,
    bid: point.bid,
    mid: point.mid,
    ask: point.ask,
  };
}

function interpolationAnchorCopy(point: FxForwardPoint): FxForwardInterpolationAnchor {
  return exactPointCopy(point);
}

function interpolateOptionalFxRate(
  left: number | null,
  right: number | null,
  weight: number
): number | null {
  return left == null || right == null ? null : interpolateFxRate(left, right, weight);
}

function interpolateFxRate(left: number, right: number, weight: number): number {
  return Number((left + ((right - left) * weight)).toFixed(8));
}

export interface ParsedFxPaste {
  points: FxForwardPoint[];
  errors: string[];
}

/** Accept `YYYY-MM mid` or `YYYY-MM bid mid ask`, with an optional header row. */
export function parseFxCurvePaste(text: string): ParsedFxPaste {
  const points = new Map<string, FxForwardPoint>();
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!;
    const fields = line.split(/[\t;, ]+/).map((field) => field.trim()).filter(Boolean);
    if (fields.some((field) => /delivery|month|mid|rate/i.test(field)) && !/^\d{4}-\d{2}$/.test(fields[0] ?? "")) {
      continue;
    }
    const month = fields[0] ?? "";
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      errors.push(`Line ${index + 1}: delivery month must use YYYY-MM.`);
      continue;
    }
    let bid: number | null = null;
    let mid: number | null = null;
    let ask: number | null = null;
    if (fields.length === 2) {
      mid = numberOf(fields[1]);
    } else if (fields.length === 4) {
      bid = numberOf(fields[1]);
      mid = numberOf(fields[2]);
      ask = numberOf(fields[3]);
    } else {
      errors.push(`Line ${index + 1}: use “YYYY-MM mid” or “YYYY-MM bid mid ask”.`);
      continue;
    }
    if (mid == null || mid <= 0 || (bid != null && bid <= 0) || (ask != null && ask <= 0)) {
      errors.push(`Line ${index + 1}: FX values must be positive numbers.`);
      continue;
    }
    if ((bid != null && bid > mid) || (ask != null && ask < mid)) {
      errors.push(`Line ${index + 1}: expected bid ≤ mid ≤ ask.`);
      continue;
    }
    points.set(month, { deliveryMonth: month, bid, mid, ask });
  }
  return { points: [...points.values()].sort((a, b) => a.deliveryMonth.localeCompare(b.deliveryMonth)), errors };
}

export function isoToday(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function currentMonth(): string {
  return isoToday().slice(0, 7);
}

function monthOrdinal(month: string): number | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!match) return null;
  return (Number(match[1]) * 12) + Number(match[2]) - 1;
}

export function addMonth(month: string, offset: number): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}
